import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Plus, Edit2, Trash2, X, Truck, CreditCard, TrendingUp, FileDown } from "lucide-react";

const SOURCES = ["Delhivery", "Shiprocket", "DTDC", "BlueDart", "Razorpay", "Shopify Payments", "PayU", "UPI", "Bank Transfer", "Cash", "Other"];
const TYPES = ["COD", "PREPAID"];

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};

export default function Collections() {
  const [activeTab, setActiveTab] = useState("COD");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  async function loadRows() {
    try {
      const params = { type: activeTab };
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/collections", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function loadSummary() {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/collections/summary", { params });
      setSummary(data);
    } catch (_) {}
  }
  useEffect(() => { loadRows(); loadSummary(); /* eslint-disable-next-line */ }, [activeTab, search, dateFrom, dateTo]);

  async function onDelete(row) {
    if (!window.confirm(`Delete this ${row.type} collection of ₹${row.amount.toLocaleString("en-IN")}?`)) return;
    try {
      await api.delete(`/collections/${row.id}`);
      toast.success("Deleted"); loadRows(); loadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  const total = rows.reduce((a, r) => a + (r.amount || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="collections-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ FINANCE · MONEY IN</div>
          <h1 className="section-title text-5xl lg:text-6xl">Collections.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">Log every COD remittance from couriers and every prepaid payout from gateways. Reconcile against your Daily Sales.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="brut-btn px-5 py-2.5 inline-flex items-center gap-2 self-start" data-testid="new-collection-btn">
          <Plus size={16} /> New Entry
        </button>
      </header>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard testid="stat-cod" icon={Truck} label="COD received" value={`₹${summary.total_cod.toLocaleString("en-IN")}`} />
          <StatCard testid="stat-prepaid" icon={CreditCard} label="Prepaid received" value={`₹${summary.total_prepaid.toLocaleString("en-IN")}`} />
          <StatCard testid="stat-total" icon={TrendingUp} label="Grand total" value={`₹${summary.grand_total.toLocaleString("en-IN")}`} accent />
          <StatCard testid="stat-pending" icon={Wallet} label="COD pending"
            value={`₹${(summary.cod_pending || 0).toLocaleString("en-IN")}`}
            sub={`Sales expect ₹${(summary.expected_cod_from_sales || 0).toLocaleString("en-IN")}`}
            danger={summary.cod_pending > 0} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-5" data-testid="collections-tabs">
        {TYPES.map((t) => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-5 py-2.5 border-2 border-black font-mono-ds text-xs uppercase tracking-widest font-bold ${activeTab === t ? "bg-black text-white shadow-[3px_3px_0_0_#0a0a0a]" : "bg-white hover:bg-zinc-100"}`}
            data-testid={`tab-${t}`}>
            {t === "COD" ? "COD Collections" : "Prepaid Collections"}
          </button>
        ))}
      </div>

      {/* Filter strip */}
      <div className="brut-card p-4 mb-5 flex flex-wrap items-center gap-3">
        <input className="brut-input max-w-xs" placeholder="Search source, ref#, order, notes…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="collections-search" />
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">From</div>
        <input type="date" className="brut-input font-mono-ds max-w-[180px]" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="collections-date-from" />
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">To</div>
        <input type="date" className="brut-input font-mono-ds max-w-[180px]" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="collections-date-to" />
        <button onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); }} className="px-3 py-2 border-2 border-black font-mono-ds text-[11px] uppercase tracking-wider bg-white hover:bg-zinc-100" data-testid="collections-clear-filters">Clear</button>
      </div>

      {/* Table */}
      <div className="brut-card overflow-x-auto">
        <table className="brut-table min-w-[900px]">
          <thead>
            <tr>
              <th>Date</th><th>Source</th><th>Amount (₹)</th><th>Reference #</th>
              <th>Orders covered</th><th>Notes</th>
              <th className="w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`collection-row-${r.id}`}>
                <td className="font-mono-ds text-xs whitespace-nowrap">{r.date}</td>
                <td className="font-display font-bold">{r.source || "—"}</td>
                <td className="font-mono-ds font-black">₹{r.amount.toLocaleString("en-IN")}</td>
                <td className="font-mono-ds text-xs">{r.reference_no || "—"}</td>
                <td className="text-xs max-w-[220px] truncate" title={(r.order_nos || []).join(", ")}>{(r.order_nos || []).join(", ") || "—"}</td>
                <td className="text-xs max-w-[260px] truncate" title={r.notes}>{r.notes || "—"}</td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(r); setShowForm(true); }} className="p-1.5 border-2 border-black hover:bg-zinc-100" data-testid={`edit-collection-${r.id}`}><Edit2 size={12} /></button>
                    <button onClick={() => onDelete(r)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" data-testid={`delete-collection-${r.id}`}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-zinc-500 font-mono-ds text-xs uppercase">No {activeTab} entries yet · Click &quot;New Entry&quot;</td></tr>}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="bg-black text-white font-display font-black" data-testid="collections-totals">
                <td colSpan={2} className="text-right pr-4 font-mono-ds text-[11px] tracking-widest uppercase">Total →</td>
                <td className="font-mono-ds font-black text-base" data-testid="collections-totals-amt">₹{total.toLocaleString("en-IN")}</td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showForm && <CollectionForm initial={editing} defaultType={activeTab} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); loadRows(); loadSummary(); }} />}
    </div>
  );
}

function CollectionForm({ initial, defaultType, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    date: initial?.date || todayIST(),
    type: initial?.type || defaultType || "COD",
    source: initial?.source || SOURCES[0],
    amount: initial?.amount || "",
    reference_no: initial?.reference_no || "",
    order_nos: (initial?.order_nos || []).join(", "),
    notes: initial?.notes || "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      const payload = {
        date: form.date,
        type: form.type,
        source: form.source,
        amount: amt,
        reference_no: form.reference_no.trim(),
        order_nos: form.order_nos.split(",").map((s) => s.trim()).filter(Boolean),
        notes: form.notes,
      };
      if (isEdit) await api.put(`/collections/${initial.id}`, payload);
      else await api.post("/collections", payload);
      toast.success(isEdit ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="collection-form-modal">
      <div className="brut-card bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b-2 border-black flex items-center justify-between">
          <div className="font-display font-black text-xl">{isEdit ? "Edit" : "New"} Collection</div>
          <button onClick={onClose} className="p-1.5 border-2 border-black hover:bg-zinc-100" data-testid="close-collection-form"><X size={14} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Date">
            <input type="date" className="brut-input font-mono-ds" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="collection-date" />
          </Field>
          <Field label="Type">
            <select className="brut-input" value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="collection-type">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Source">
            <select className="brut-input" value={form.source} onChange={(e) => set("source", e.target.value)} data-testid="collection-source">
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Amount (₹)">
            <input type="number" step="0.01" className="brut-input font-mono-ds" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="collection-amount" placeholder="0.00" />
          </Field>
          <Field label="Reference # (UTR / Payment ID)">
            <input className="brut-input font-mono-ds" value={form.reference_no} onChange={(e) => set("reference_no", e.target.value)} data-testid="collection-ref" placeholder="e.g. UTR2026091200XY" />
          </Field>
          <Field label="Orders covered (comma-separated)">
            <input className="brut-input font-mono-ds" value={form.order_nos} onChange={(e) => set("order_nos", e.target.value)} data-testid="collection-orders" placeholder="CA-1005, CA-1006" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea className="brut-input" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="collection-notes" />
            </Field>
          </div>
        </div>
        <div className="p-4 border-t-2 border-black flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2.5" data-testid="cancel-collection-btn">Cancel</button>
          <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5" data-testid="save-collection-btn">{busy ? "Saving…" : (isEdit ? "Save" : "Add Entry")}</button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent, danger, icon: Icon, testid }) {
  const bg = danger ? "!bg-[#FFF0F0]" : accent ? "!bg-black text-white" : "bg-white";
  const sublight = accent ? "text-white/70" : danger ? "text-[#FF3333]" : "text-zinc-500";
  return (
    <div className={`brut-card p-5 ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className={sublight} />}
        <div className={`font-mono-ds text-[10px] tracking-widest uppercase ${sublight}`}>{label}</div>
      </div>
      <div className={`font-display font-black text-3xl leading-tight ${accent ? "text-white" : ""}`}>{value}</div>
      {sub && <div className={`font-mono-ds text-[10px] mt-1 ${sublight}`}>{sub}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
